import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, ChevronRight, User, Zap, BarChart3, Users, Clock, CheckCircle, XCircle } from 'lucide-react';
import { useDemoStore } from '@/store/demoStore';
import { demoProposals, demoTreasuryData, governanceStats, getProposalStateName } from '@/services/governanceData';
import PageLayout from '@/components/layout/PageLayout';
import PageHeader from '@/components/layout/PageHeader';

const GovernanceNew: React.FC = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [selectedProposal, setSelectedProposal] = useState<any>(null);
  const { isDemoMode } = useDemoStore();

  // Format large numbers with commas
  const formatNumber = (num: string) => {
    return parseInt(num).toLocaleString();
  };

  // Calculate vote percentages
  const calculatePercentage = (votes: string, total: number) => {
    const votesNum = parseInt(votes);
    return total > 0 ? Math.round((votesNum / total) * 100) : 0;
  };

  const handleProposalClick = (proposal: any) => {
    setSelectedProposal(proposal);
  };

  return (
    <PageLayout>
      <PageHeader 
        title="Governance" 
        description="Participate in platform governance and help shape the future of FTTF.World"
      />
      
      <div className="container-custom">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="glass-card shadow-3d">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <BarChart3 className="mr-2 h-5 w-5 text-primary-500" />
                  Proposals
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gradient">{governanceStats.totalProposals}</p>
                <p className="text-sm text-gray-400">Total proposals</p>
                <p className="text-sm text-primary-400 mt-1">
                  {governanceStats.activeProposals} active now
                </p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="glass-card shadow-3d">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <Users className="mr-2 h-5 w-5 text-primary-500" />
                  Participation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gradient">{governanceStats.voterParticipation}%</p>
                <p className="text-sm text-gray-400">Voter participation</p>
                <p className="text-sm text-primary-400 mt-1">
                  {formatNumber(governanceStats.totalVotes.toString())} total votes
                </p>
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card className="glass-card shadow-3d">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <Clock className="mr-2 h-5 w-5 text-primary-500" />
                  Voting Period
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gradient">7 Days</p>
                <p className="text-sm text-gray-400">Standard voting period</p>
                {isDemoMode && (
                  <p className="text-sm text-primary-400 mt-1">
                    ~40,320 blocks
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card className="glass-card shadow-3d">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center text-lg">
                  <Users className="mr-2 h-5 w-5 text-primary-500" />
                  Quorum
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold text-gradient">{governanceStats.quorum}%</p>
                <p className="text-sm text-gray-400">Minimum participation required</p>
                {isDemoMode && (
                  <p className="text-sm text-primary-400 mt-1">
                    {governanceStats.proposalThreshold.toLocaleString()} FTTF to propose
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
        
        {/* Proposals */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Tabs defaultValue="active" onValueChange={setActiveTab}>
            <TabsList className="mb-4 bg-dark-800/50 backdrop-blur-sm">
              <TabsTrigger value="active" className="data-[state=active]:bg-primary-600/30">Active Proposals</TabsTrigger>
              <TabsTrigger value="all" className="data-[state=active]:bg-primary-600/30">All Proposals</TabsTrigger>
              {isDemoMode && <TabsTrigger value="treasury" className="data-[state=active]:bg-primary-600/30">Treasury</TabsTrigger>}
            </TabsList>
            
            <TabsContent value="active">
              <div className="grid grid-cols-1 gap-4">
                {demoProposals.filter(p => p.state === 1).length > 0 ? (
                  demoProposals.filter(p => p.state === 1).map(proposal => (
                    <motion.div
                      key={proposal.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ scale: 1.01 }}
                    >
                      <Card 
                        className="cursor-pointer glass-card glass-hover shadow-3d shadow-3d-hover" 
                        onClick={() => handleProposalClick(proposal)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-gradient">
                              {isDemoMode ? `FTTF-${proposal.id}` : `Proposal #${proposal.id}`}
                            </CardTitle>
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-primary-600/20 text-primary-400">
                              {getProposalStateName(proposal.state)}
                            </span>
                          </div>
                          <CardDescription className="truncate mt-1">
                            {proposal.summary}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pb-2">
                          <div className="flex justify-between text-sm text-gray-400">
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {`Ends in ${Math.ceil((new Date(proposal.endTime).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days`}
                            </span>
                            <span className="flex items-center">
                              <User className="h-4 w-4 mr-1" />
                              {proposal.proposerName}
                            </span>
                          </div>
                        </CardContent>
                        <CardFooter className="pt-2 pb-3">
                          <Button variant="ghost" size="sm" className="ml-auto">
                            View Details <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))
                ) : (
                  <p className="text-center py-8 text-gray-400">No active proposals</p>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="all">
              <div className="grid grid-cols-1 gap-4">
                {demoProposals.length > 0 ? (
                  demoProposals.map(proposal => (
                    <motion.div
                      key={proposal.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                      whileHover={{ scale: 1.01 }}
                    >
                      <Card 
                        className="cursor-pointer glass-card glass-hover shadow-3d shadow-3d-hover" 
                        onClick={() => handleProposalClick(proposal)}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-center">
                            <CardTitle className="text-gradient">
                              {isDemoMode ? `FTTF-${proposal.id}` : `Proposal #${proposal.id}`}
                            </CardTitle>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              proposal.state === 1 ? 'bg-primary-600/20 text-primary-400' :
                              proposal.state === 4 || proposal.state === 7 ? 'bg-green-500/20 text-green-500' :
                              proposal.state === 3 ? 'bg-red-500/20 text-red-500' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {getProposalStateName(proposal.state)}
                            </span>
                          </div>
                          <CardDescription className="truncate mt-1">
                            {proposal.summary}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pb-2">
                          <div className="flex justify-between text-sm text-gray-400">
                            <span className="flex items-center">
                              <Calendar className="h-4 w-4 mr-1" />
                              {proposal.state === 1 ? 
                                `Ends in ${Math.ceil((new Date(proposal.endTime).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} days` : 
                                `Ended ${Math.floor((Date.now() - new Date(proposal.endTime).getTime()) / (1000 * 60 * 60 * 24))} days ago`
                              }
                            </span>
                            <span className="flex items-center">
                              <User className="h-4 w-4 mr-1" />
                              {proposal.proposerName}
                            </span>
                          </div>
                        </CardContent>
                        <CardFooter className="pt-2 pb-3">
                          <Button variant="ghost" size="sm" className="ml-auto">
                            View Details <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </CardFooter>
                      </Card>
                    </motion.div>
                  ))
                ) : (
                  <p className="text-center py-8 text-gray-400">No proposals found</p>
                )}
              </div>
            </TabsContent>
            
            {isDemoMode && (
              <TabsContent value="treasury">
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Card className="glass-card shadow-3d mb-6">
                    <CardHeader>
                      <CardTitle className="text-gradient">Treasury Assets</CardTitle>
                      <CardDescription>Current balance and distribution of treasury assets</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-2">Total Value: {demoTreasuryData.totalValueUSD}</h3>
                        <div className="w-full h-4 bg-dark-700 rounded-full overflow-hidden">
                          {demoTreasuryData.tokenBalances.map((token, index) => (
                            <div 
                              key={token.token}
                              className="h-full float-left"
                              style={{ 
                                width: `${token.percentage}%`, 
                                backgroundColor: index === 0 ? '#8A2BE2' : index === 1 ? '#2563EB' : '#10B981'
                              }}
                            />
                          ))}
                        </div>
                        <div className="flex justify-between mt-2">
                          {demoTreasuryData.tokenBalances.map((token) => (
                            <div key={token.token} className="flex items-center">
                              <div 
                                className="w-3 h-3 rounded-full mr-1"
                                style={{ 
                                  backgroundColor: token.token === 'FTTF' ? '#8A2BE2' : 
                                                  token.token === 'DUSD' ? '#2563EB' : '#10B981'
                                }}
                              />
                              <span className="text-sm">{token.token}: {token.percentage}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        {demoTreasuryData.tokenBalances.map((token) => (
                          <div key={token.token} className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-dark-600 flex items-center justify-center mr-3">
                                <img src={token.logoUrl} alt={token.token} className="w-6 h-6" />
                              </div>
                              <div>
                                <h4 className="font-medium">{token.token}</h4>
                                <p className="text-sm text-gray-400">{token.symbol}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-medium">{token.balance}</p>
                              <p className="text-sm text-gray-400">${token.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="glass-card shadow-3d">
                    <CardHeader>
                      <CardTitle className="text-gradient">Recent Transactions</CardTitle>
                      <CardDescription>Latest treasury transactions</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {demoTreasuryData.recentTransactions.slice(0, 5).map((tx) => (
                          <div key={tx.id} className="flex items-center justify-between p-3 bg-dark-700 rounded-lg">
                            <div className="flex items-center">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                                tx.type === 'income' ? 'bg-green-500/20' : 'bg-red-500/20'
                              }`}>
                                <Zap className={tx.type === 'income' ? 'text-green-500' : 'text-red-500'} size={20} />
                              </div>
                              <div>
                                <h4 className="font-medium">{tx.description}</h4>
                                <p className="text-xs text-gray-400">
                                  {new Date(tx.timestamp).toLocaleDateString()} • 
                                  {tx.from.substring(0, 6)}...{tx.from.substring(38)}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-medium ${tx.type === 'income' ? 'text-green-500' : 'text-red-500'}`}>
                                {tx.type === 'income' ? '+' : '-'}{tx.value} {tx.tokenSymbol}
                              </p>
                              <p className="text-xs text-gray-400">
                                <a 
                                  href={`https://explorer.blaze.soniclabs.com/tx/${tx.hash}`} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="hover:text-primary-500"
                                >
                                  View Transaction
                                </a>
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </TabsContent>
            )}
          </Tabs>
        </motion.div>
        
        {/* Selected Proposal Details */}
        {selectedProposal && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            <Card className="glass-card shadow-3d">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>FTTF-{selectedProposal.id}: {selectedProposal.title}</CardTitle>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    selectedProposal.state === 1 ? 'bg-primary-600/20 text-primary-400' :
                    selectedProposal.state === 4 || selectedProposal.state === 7 ? 'bg-green-500/20 text-green-500' :
                    selectedProposal.state === 3 ? 'bg-red-500/20 text-red-500' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {getProposalStateName(selectedProposal.state)}
                  </span>
                </div>
                <CardDescription>{selectedProposal.summary}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="p-4 bg-dark-800 rounded-lg">
                    <h3 className="font-semibold mb-2">Description</h3>
                    <p className="text-gray-300">{selectedProposal.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <h3 className="font-semibold">Proposer</h3>
                      <div className="flex items-center mt-1">
                        {selectedProposal.proposerAvatar && (
                          <img 
                            src={selectedProposal.proposerAvatar} 
                            alt={selectedProposal.proposerName} 
                            className="w-6 h-6 rounded-full mr-2"
                          />
                        )}
                        <p>{selectedProposal.proposerName}</p>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{selectedProposal.proposer.substring(0, 6)}...{selectedProposal.proposer.substring(38)}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold">Voting Period</h3>
                      <p>From {new Date(selectedProposal.startTime).toLocaleDateString()} to {new Date(selectedProposal.endTime).toLocaleDateString()}</p>
                      <p className="text-xs text-gray-400 mt-1">Blocks {selectedProposal.startBlock} - {selectedProposal.endBlock}</p>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold">Status</h3>
                      <p>{getProposalStateName(selectedProposal.state)}</p>
                      {selectedProposal.executionTime && (
                        <p className="text-xs text-gray-400 mt-1">
                          {selectedProposal.state === 7 ? 'Executed on ' : 'Scheduled for '}
                          {new Date(selectedProposal.executionTime).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3">Votes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Calculate total votes */}
                      {(() => {
                        const forVotes = parseInt(selectedProposal.forVotes);
                        const againstVotes = parseInt(selectedProposal.againstVotes);
                        const abstainVotes = parseInt(selectedProposal.abstainVotes);
                        const totalVotes = forVotes + againstVotes + abstainVotes;
                        
                        return (
                          <>
                            <div className="bg-green-900/20 p-4 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center">
                                  <CheckCircle className="text-green-500 mr-2" size={16} />
                                  <span className="text-green-500 font-medium">For</span>
                                </div>
                                <span className="text-green-500 font-bold">{calculatePercentage(selectedProposal.forVotes, totalVotes)}%</span>
                              </div>
                              <div className="w-full bg-dark-700 rounded-full h-2.5 mb-2">
                                <div 
                                  className="bg-green-500 h-2.5 rounded-full" 
                                  style={{ width: `${calculatePercentage(selectedProposal.forVotes, totalVotes)}%` }}
                                ></div>
                              </div>
                              <p className="text-sm text-gray-300">{formatNumber(selectedProposal.forVotes)} votes</p>
                            </div>
                            
                            <div className="bg-red-900/20 p-4 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center">
                                  <XCircle className="text-red-500 mr-2" size={16} />
                                  <span className="text-red-500 font-medium">Against</span>
                                </div>
                                <span className="text-red-500 font-bold">{calculatePercentage(selectedProposal.againstVotes, totalVotes)}%</span>
                              </div>
                              <div className="w-full bg-dark-700 rounded-full h-2.5 mb-2">
                                <div 
                                  className="bg-red-500 h-2.5 rounded-full" 
                                  style={{ width: `${calculatePercentage(selectedProposal.againstVotes, totalVotes)}%` }}
                                ></div>
                              </div>
                              <p className="text-sm text-gray-300">{formatNumber(selectedProposal.againstVotes)} votes</p>
                            </div>
                            
                            <div className="bg-gray-700/20 p-4 rounded-lg">
                              <div className="flex justify-between items-center mb-2">
                                <div className="flex items-center">
                                  <span className="w-4 h-4 rounded-full border-2 border-gray-500 mr-2"></span>
                                  <span className="text-gray-400 font-medium">Abstain</span>
                                </div>
                                <span className="text-gray-400 font-bold">{calculatePercentage(selectedProposal.abstainVotes, totalVotes)}%</span>
                              </div>
                              <div className="w-full bg-dark-700 rounded-full h-2.5 mb-2">
                                <div 
                                  className="bg-gray-500 h-2.5 rounded-full" 
                                  style={{ width: `${calculatePercentage(selectedProposal.abstainVotes, totalVotes)}%` }}
                                ></div>
                              </div>
                              <p className="text-sm text-gray-300">{formatNumber(selectedProposal.abstainVotes)} votes</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  {selectedProposal.transactions && selectedProposal.transactions.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Transactions</h3>
                      <div className="space-y-3">
                        {selectedProposal.transactions.map((tx: any, index: number) => (
                          <div key={index} className="p-3 bg-dark-800 rounded-lg">
                            <div className="flex justify-between mb-1">
                              <span className="text-sm font-medium">Target</span>
                              <span className="text-sm">{tx.target}</span>
                            </div>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm font-medium">Function</span>
                              <span className="text-sm font-mono text-primary-400">{tx.signature}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm font-medium">Description</span>
                              <span className="text-sm">{tx.description}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button variant="outline" onClick={() => setSelectedProposal(null)}>
                  Back to List
                </Button>
                
                {selectedProposal.state === 1 && (
                  <div className="flex space-x-2">
                    <Button variant="destructive">Vote Against</Button>
                    <Button variant="default">Vote For</Button>
                  </div>
                )}
              </CardFooter>
            </Card>
          </motion.div>
        )}
      </div>
    </PageLayout>
  );
};

export default GovernanceNew;